// CJ Dropshipping pricing engine -- the ONE formula used by the Shipping
// Cost Calculation tab, the Sync / Resync wizard and the per-country Sales
// Simulation. Pure (no React), so it can be tested with node --test.
// server_elakay keeps the same formula in
// dropShipping/CJDropshipping/services/cjPricing.service.js (separate Node
// project); both copies are pinned by the same numeric fixtures
// (tests/cjPricing.test.js on each side).

const round2 = (value) => Number((Number(value) || 0).toFixed(2));

// Taux/fixes réels payés par produit CJ Dropshipping vendu -- Stripe
// (paiement) + Elakay (plateforme). Constantes locales à CE fichier (pas
// allPriceFeet.jsx, le moteur partagé pour salesSimulation.jsx/produits
// natifs, avec un taux Elakay PAR CATÉGORIE et un tout autre modèle -- pas ce
// qu'on veut ici : ce fichier est désormais un moteur CJ Dropshipping
// autonome, ne dépend plus d'allPriceFeet.jsx du tout).
const STRIPE_FEE_RATE = 0.029;
const STRIPE_FEE_FIXED = 0.30;
const ELAKAY_FEE_RATE = 0.06;
const ELAKAY_FEE_FIXED = 0.50;
const VARIABLE_FEE_RATE = STRIPE_FEE_RATE + ELAKAY_FEE_RATE;
const FIXED_FEES = STRIPE_FEE_FIXED + ELAKAY_FEE_FIXED;
// Taux par défaut tant qu'aucun code postal n'a résolu de zone de taxe réelle
// (même valeur que l'ancien CheckTaxZone d'allPriceFeet.jsx, pour ne rien
// changer au comportement déjà observé par les sellers).
const DEFAULT_TAX_RATE = 0.1045;

// ============================================================================
// MOTEUR DE CALCUL CENTRAL -- fonction UNIQUE utilisée pour les deux modes.
// Aucune autre fonction de ce fichier ne calcule frais/taxes/profit : tout
// passe par ici, pour qu'il n'y ait jamais deux résultats divergents pour la
// même situation.
//
// Le client ne voit que : prix du produit, frais de livraison (0 $ ou un
// montant), taxes, total à payer. En arrière-plan, la fonction calcule
// toujours : coût produit CJ, coût livraison CJ, frais Stripe, frais Elakay,
// taxes, profit désiré -- que `shippingChargedToCustomer` soit 0 $ (livraison
// "gratuite", coût absorbé dans le prix produit) ou un montant (livraison
// facturée à part, généralement = cjShippingCost, jamais remargée) ne change
// JAMAIS la formule : ce n'est qu'un paramètre numérique normal, jamais une
// branche conditionnelle séparée. Le prix de vente et le montant facturé pour
// la livraison ne sont que deux façons différentes de présenter EXACTEMENT le
// même total au client -- l'économie du vendeur (frais, revenu net, profit)
// est identique dans les deux cas.
//
// mode === "sellingPrice" (Mode 1) : l'utilisateur saisit desiredProfit, la
//   fonction résout le sellingPrice (prix affiché, avant remise) nécessaire
//   pour garantir EXACTEMENT ce profit, tous frais déduits. Formule fermée
//   (aucune approximation itérative, aucune taxe repliée dedans -- les taxes
//   ne sont jamais un revenu/profit vendeur, calculées séparément) :
//
//     chargedPrice + S = (cjProductCost + cjShippingCost + desiredProfit + fixedFees)
//                         ÷ (1 - variableFeeRate)
//     sellingPrice = (chargedPrice + S - S) ÷ (1 - discount%)
//
// mode === "profit" (Mode 2) : l'utilisateur saisit sellingPrice directement,
//   la fonction dérive le profit réel avec EXACTEMENT la même logique de
//   frais/taxes -- aucune divergence possible entre les deux modes.
//
// Arrondi UNIQUEMENT à la fin (jamais sur une étape intermédiaire) --
// `round2` n'est appliqué qu'au moment de construire l'objet retourné.
const calculateCJPricing = ({
  mode, // "sellingPrice" | "profit"
  cjProductCost = 0,
  cjShippingCost = 0,
  shippingChargedToCustomer = 0,
  discountPercent = 0,
  desiredProfit = 0, // requis en mode "sellingPrice"
  sellingPrice: inputSellingPrice = 0, // requis en mode "profit"
  taxRate = DEFAULT_TAX_RATE,
}) => {
  const S = Number(shippingChargedToCustomer) || 0;
  const d = Math.min(Math.max(Number(discountPercent) || 0, 0), 99) / 100;

  let sellingPrice;
  if (mode === "sellingPrice") {
    const chargedPricePlusShipping = (cjProductCost + cjShippingCost + Number(desiredProfit || 0) + FIXED_FEES) / (1 - VARIABLE_FEE_RATE);
    const chargedPrice = chargedPricePlusShipping - S;
    sellingPrice = d < 1 ? chargedPrice / (1 - d) : chargedPrice;
  } else {
    sellingPrice = Number(inputSellingPrice) || 0;
  }

  const chargedPrice = sellingPrice * (1 - d); // prix réellement payé par le client pour le produit, après remise
  const feeBase = chargedPrice + S; // Stripe/Elakay prennent leur pourcentage sur TOUT ce qui est facturé (produit + livraison), pas seulement le produit
  const stripeFee = feeBase * STRIPE_FEE_RATE + STRIPE_FEE_FIXED;
  // Détail Elakay (service/selling/referral) -- simple DÉCOMPOSITION du même
  // taux ELAKAY_FEE_RATE en 3 sous-lignes d'affichage (fractions 3/12+5/12+4/12
  // = 12/12, soit 100% du taux), pour la transparence du tableau détaillé.
  // Ce ne sont PAS 3 frais distincts qui s'additionnent en plus d'Elakay --
  // elakayFee reste calculé une seule fois, ci-dessous, à partir de la somme
  // de ces 3 sous-lignes + le frais fixe de clôture.
  const elakayServiceFee = feeBase * ELAKAY_FEE_RATE * (3 / 12);
  const elakaySellingFee = feeBase * ELAKAY_FEE_RATE * (5 / 12);
  const elakayReferralFee = feeBase * ELAKAY_FEE_RATE * (4 / 12);
  const elakayClosingFee = ELAKAY_FEE_FIXED;
  const elakayFee = elakayServiceFee + elakaySellingFee + elakayReferralFee + elakayClosingFee;
  const totalFees = stripeFee + elakayFee;
  const netRevenue = feeBase - totalFees; // revenu vendeur après Stripe+Elakay, avant coût produit/livraison
  const profit = netRevenue - cjProductCost - cjShippingCost;
  const taxes = feeBase * taxRate; // jamais un revenu/coût vendeur -- ajoutées séparément au checkout
  const customerTotal = feeBase + taxes;

  return {
    cjProductCost: round2(cjProductCost),
    cjShippingCost: round2(cjShippingCost),
    shippingChargedToCustomer: round2(S),
    discountPercent: Number((d * 100).toFixed(2)),
    sellingPrice: round2(sellingPrice),
    chargedPrice: round2(chargedPrice),
    stripeFee: round2(stripeFee),
    elakayServiceFee: round2(elakayServiceFee),
    elakaySellingFee: round2(elakaySellingFee),
    elakayReferralFee: round2(elakayReferralFee),
    elakayClosingFee: round2(elakayClosingFee),
    elakayFee: round2(elakayFee),
    totalFees: round2(totalFees),
    taxes: round2(taxes),
    customerTotal: round2(customerTotal),
    netRevenue: round2(netRevenue),
    profit: round2(profit),
  };
};

module.exports = {
  STRIPE_FEE_RATE,
  STRIPE_FEE_FIXED,
  ELAKAY_FEE_RATE,
  ELAKAY_FEE_FIXED,
  DEFAULT_TAX_RATE,
  calculateCJPricing,
};
